import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { AI_RESPONSE_TYPES } from 'constants/AI';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { FormField } from 'components/_basic/Form/FormField';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { BorderedCard } from 'components/BorderedCard';

import { concatWithDot } from 'helpers/string';

import { SwotAnalysisRow } from './SwotAnalysisRow';
import { ABOUT_COMPANY_SWOT_NAMES, analysisRowInitialValues, prefix } from './utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

import styles from './SWOT.module.scss';

export const SWOT: FC = () => {
  const { t } = useTranslation();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.ABOUT_COMPANY,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.SWOT,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('company.title')}
          badgeText={t('company.swot.title')}
          section={FOR_BANK_SECTIONS.ABOUT_COMPANY}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.swot.swotAnalysis')}
        aiFieldType={AI_RESPONSE_TYPES.COMPLEX}
        aiFieldName={prefix}
        collapsible
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('strength')}>
                <FieldArrayComponent
                  name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('strength')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('opportunity')}>
                <FieldArrayComponent
                  name={concatWithDot(prefix, ABOUT_COMPANY_SWOT_NAMES.OPPORTUNITIES)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('opportunity')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('weakness')}>
                <FieldArrayComponent
                  name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('weakness')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('threat')}>
                <FieldArrayComponent
                  name={concatWithDot(prefix, ABOUT_COMPANY_SWOT_NAMES.THREATS)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('threat')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
};
